/**
 * WorkbookListsSections.jsx - the GAT-B quote workbook's "0. INFO" lists on
 * Engineers > Quote Rates (main card only, 10/12/26): the customer bill rates
 * per skill level (with OTRates?), skill levels and types, the WorkSheet
 * tasks and which are Fixed Fee, the per-day expense defaults, employee
 * locations with a round-trip flight, and the Project Manager / Eng. Manager
 * -> customer lists. They are part of the rate card and save with it.
 * Pay and burden are NOT here: they are Engineers > Staff cost rates.
 */
import EditableTable from './EditableTable';
import { Field, Grid, NumInput, Section, inputStyle } from '../quotes/quoteUi';

const PER_DAY = [
  ['hotel', 'Hotel'],
  ['transport', 'Transport'],
  ['meals', 'Meals'],
  ['parking', 'Parking'],
  ['laundry', 'Laundry'],
  ['misc', 'Misc'],
];

function LinesBox({ label, value, onChange, ro, rows = 6 }) {
  return (
    <Field label={label}>
      <textarea
        aria-label={label}
        rows={rows}
        readOnly={ro}
        value={(value || []).join('\n')}
        onChange={(e) => onChange(e.target.value.split('\n'))}
        style={{ ...inputStyle, resize: 'vertical' }}
      />
    </Field>
  );
}

export default function WorkbookListsSections({ s, setDraft, ro, cur }) {
  const setPart = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const ws = s.worksheet || {};
  const setWs = (k) => (v) => setDraft((d) => ({ ...d, worksheet: { ...(d.worksheet || {}), [k]: v } }));
  const setPerDay = (side, k) => (v) =>
    setDraft((d) => ({
      ...d,
      worksheet: { ...(d.worksheet || {}), [side]: { ...((d.worksheet || {})[side] || {}), [k]: v } },
    }));
  const sym = cur.symbol;
  return (
    <>
      <Section
        title="Customer bill rates per skill level"
        hint="The Worksheet quote bills each engineer at their customer's rate for their level. OTRates? No = that customer buys straight time only (no 1.3 / 1.7); blank or Yes = overtime applies."
      >
        <EditableTable
          label="Customer bill rates"
          readOnly={ro}
          rows={s.customerRates || []}
          onChange={setPart('customerRates')}
          blank={{ customer: '', otRates: 'Yes', level1: '', level2: '', level3: '' }}
          columns={[
            { key: 'customer', label: 'Customer', width: 220 },
            { key: 'otRates', label: 'OTRates?', type: 'select', options: ['Yes', 'No', ''], width: 90 },
            { key: 'level1', label: `Level I (${sym}/hr)`, type: 'number', width: 120 },
            { key: 'level2', label: `Level II (${sym}/hr)`, type: 'number', width: 120 },
            { key: 'level3', label: `Level III (${sym}/hr)`, type: 'number', width: 120 },
          ]}
        />
      </Section>

      <Section
        title="Worksheet quote"
        hint="The Worksheet quote's task list (its Summary rows, in order), which tasks the proposal prices as Fixed Fee (the rest are T&E), and the expense flags."
      >
        <Grid cols={3}>
          <LinesBox label="Tasks" value={ws.tasks} onChange={setWs('tasks')} ro={ro} rows={10} />
          <LinesBox label="Fixed Fee tasks" value={ws.fixedFeeTasks} onChange={setWs('fixedFeeTasks')} ro={ro} />
          <LinesBox label="Expense flags" value={ws.expenseFlags} onChange={setWs('expenseFlags')} ro={ro} rows={4} />
        </Grid>
      </Section>

      <Section
        title="Expenses per day (Worksheet quote defaults)"
        hint="What one engineer-day of expenses costs, billable to the customer and not. A quote starts with these and can change them."
      >
        <Grid cols={6}>
          {PER_DAY.map(([k, label]) => (
            <Field key={`b-${k}`} label={`Billable ${label} (${sym})`}>
              <NumInput
                aria-label={`Billable ${label} per day`}
                value={(ws.billableDefaults || {})[k]}
                disabled={ro}
                onChange={setPerDay('billableDefaults', k)}
              />
            </Field>
          ))}
          {PER_DAY.map(([k, label]) => (
            <Field key={`n-${k}`} label={`Non-billable ${label} (${sym})`}>
              <NumInput
                aria-label={`Non-billable ${label} per day`}
                value={(ws.nonBillableDefaults || {})[k]}
                disabled={ro}
                onChange={setPerDay('nonBillableDefaults', k)}
              />
            </Field>
          ))}
        </Grid>
      </Section>

      <Section title="Skill levels and types" hint="What a staff member, and a quote line with nobody named, can be.">
        <Grid cols={2}>
          <EditableTable
            label="Skill levels"
            readOnly
            rows={s.skillLevels || []}
            onChange={setPart('skillLevels')}
            columns={[
              { key: 'name', label: 'Skill level', width: 120 },
              { key: 'desc', label: 'Description' },
            ]}
          />
          <EditableTable
            label="Skill types"
            readOnly={ro}
            rows={s.skillTypes || []}
            onChange={setPart('skillTypes')}
            blank={{ name: '', desc: '' }}
            columns={[
              { key: 'name', label: 'Skill type', width: 140 },
              { key: 'desc', label: 'Description' },
            ]}
          />
        </Grid>
      </Section>

      <Section
        title="Employee locations"
        hint="Where staff are based, and a typical round-trip flight from there: a new quote line starts with it (half a flight per travel day)."
      >
        <EditableTable
          label="Employee locations"
          readOnly={ro}
          rows={s.empLocations || []}
          onChange={setPart('empLocations')}
          blank={{ name: '', flight: 0 }}
          columns={[
            { key: 'name', label: 'Location', width: 200 },
            { key: 'flight', label: `Round-trip flight (${sym})`, type: 'number', width: 160 },
          ]}
        />
      </Section>

      <Section
        title="Project managers and engineering managers"
        hint="The customer's people a quote names (the workbook's Proj.Managers and Eng. Manager lists)."
      >
        <Grid cols={2}>
          <EditableTable
            label="Project managers"
            readOnly={ro}
            rows={s.projectManagers || []}
            onChange={setPart('projectManagers')}
            blank={{ name: '', customer: '' }}
            columns={[
              { key: 'name', label: 'Project manager' },
              { key: 'customer', label: 'Customer' },
            ]}
          />
          <EditableTable
            label="Engineering managers"
            readOnly={ro}
            rows={s.engManagers || []}
            onChange={setPart('engManagers')}
            blank={{ name: '', customer: '' }}
            columns={[
              { key: 'name', label: 'Eng. manager' },
              { key: 'customer', label: 'Customer' },
            ]}
          />
        </Grid>
      </Section>
    </>
  );
}
